import { lazy, Suspense, type ComponentProps } from "react";

import type { RightPanelSurface } from "~/rightPanelStore";
import { createWorkspaceSurfaceContext } from "~/workspaceSurfaceContext";

import type { FilePreviewPanelProps } from "./FilePreviewPanel";

const FilePreviewPanel = lazy(() => import("./FilePreviewPanel"));

type FilesDescriptor = Extract<RightPanelSurface, { readonly kind: "files" | "file" }>;

export type FilesWorkspaceSurfaceContextValue = Omit<
  FilePreviewPanelProps,
  "relativePath" | "revealLine" | "revealRequestId"
>;

const filesWorkspaceSurfaceContext =
  createWorkspaceSurfaceContext<FilesWorkspaceSurfaceContextValue>();

export const FilesWorkspaceSurfaceProvider = filesWorkspaceSurfaceContext.Provider;

export interface FilesWorkspaceSurfaceProps {
  readonly descriptor: RightPanelSurface;
  readonly fallback?: ComponentProps<typeof Suspense>["fallback"];
}

/**
 * Files-owned adapter for the Workspace Surface Host renderer seam.
 *
 * The Host mounts this adapter only while a Files descriptor is active. Query,
 * editor, and tree lifetimes therefore follow the descriptor's active lifetime,
 * while the selected path remains device-local right-panel state.
 */
export function FilesWorkspaceSurface({ descriptor, fallback = null }: FilesWorkspaceSurfaceProps) {
  const context = filesWorkspaceSurfaceContext.useValue();
  if (context === null) return fallback;
  if (descriptor.kind !== "files" && descriptor.kind !== "file") return null;
  const filesDescriptor: FilesDescriptor = descriptor;

  return (
    <Suspense fallback={fallback}>
      <FilePreviewPanel
        key={`${context.environmentId}:${context.cwd}`}
        {...context}
        relativePath={filesDescriptor.kind === "file" ? filesDescriptor.relativePath : null}
        revealLine={filesDescriptor.kind === "file" ? filesDescriptor.revealLine : null}
        revealRequestId={filesDescriptor.kind === "file" ? filesDescriptor.revealRequestId : 0}
      />
    </Suspense>
  );
}
